import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, Clock, FileText, User, Calendar, AlertTriangle,
  ChevronRight, RotateCcw, Shield, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/* Status badge with appropriate colors */
const StatusBadge = ({ status }) => {
  const colors = {
    Draft: 'bg-slate-700 text-slate-300',
    InReview: 'bg-amber-900/50 text-amber-400 border-amber-600/30',
    Approved: 'bg-blue-900/50 text-blue-400 border-blue-600/30',
    Released: 'bg-emerald-900/50 text-emerald-400 border-emerald-600/30',
    Superseded: 'bg-gray-900/50 text-gray-400',
    Obsolete: 'bg-red-900/50 text-red-400',
  };
  return (
    <Badge className={`${colors[status] || colors.Draft} border`}>
      {status === 'InReview' ? 'In Review' : status}
    </Badge>
  );
};

/* Stage badge for current approval stage */
const StageBadge = ({ stage }) => {
  const labels = {
    'Engineering_Lead': 'Engineering Review',
    'Quality_Rep': 'Quality Review',
  };
  const colors = {
    'Engineering_Lead': 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    'Quality_Rep': 'bg-violet-500/20 text-violet-300 border-violet-500/40',
  };
  return (
    <Badge className={`${colors[stage] || 'bg-slate-700 text-slate-300'} border`}>
      {labels[stage] || stage}
    </Badge>
  );
};

/* Approval History Timeline */
const ApprovalTimeline = ({ approvals }) => {
  if (!approvals || approvals.length === 0) {
    return <p className="text-slate-500 text-sm italic">No approvals yet</p>;
  }
  
  return (
    <div className="space-y-3">
      {approvals.map((approval, idx) => (
        <div key={approval.approval_id || idx} className="flex items-start gap-3 p-2 rounded bg-slate-800/50">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            approval.decision === 'Approved' ? 'bg-emerald-500/20 text-emerald-400' :
            approval.decision === 'Rejected' ? 'bg-red-500/20 text-red-400' :
            'bg-amber-500/20 text-amber-400'
          }`}>
            {approval.decision === 'Approved' ? <CheckCircle className="w-4 h-4" /> :
             approval.decision === 'Rejected' ? <XCircle className="w-4 h-4" /> :
             <RotateCcw className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-200 font-medium text-sm">{approval.decision}</span>
              <StageBadge stage={approval.stage} />
            </div>
            <p className="text-slate-400 text-xs mt-1">
              by {approval.performed_by_name} • {new Date(approval.performed_at).toLocaleString()}
            </p>
            {approval.meaning_statement && (
              <p className="text-slate-500 text-xs mt-1 italic">
                "{approval.meaning_statement}"
              </p>
            )}
            {approval.comments && (
              <p className="text-slate-400 text-xs mt-1">
                Comments: {approval.comments}
              </p>
            )}
            {approval.rejection_reason && (
              <p className="text-red-400 text-xs mt-1">
                Reason: {approval.rejection_reason}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/* Pending BOM Card */
const PendingBomCard = ({ bom, onApprove, onReject, onReturnToDraft, onViewBom }) => {
  const [expanded, setExpanded] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalComment, setApprovalComment] = useState('');
  const [processing, setProcessing] = useState(false);
  
  const budgetPct = bom.budget_target > 0 ? Math.round((bom.cost_total / bom.budget_target) * 100) : 0;
  const budgetColor = budgetPct > 100 ? 'text-red-400' : budgetPct > 85 ? 'text-amber-400' : 'text-emerald-400';
  
  const handleApprove = async () => {
    setProcessing(true);
    try {
      await onApprove(bom, approvalComment);
      setApprovalComment('');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async () => {
    if (rejectReason.length < 10) {
      toast.error('Rejection reason must be at least 10 characters');
      return;
    }
    setProcessing(true);
    try {
      await onReject(bom, rejectReason);
      setShowRejectForm(false);
      setRejectReason('');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReturnToDraft = async () => {
    setProcessing(true);
    try {
      await onReturnToDraft(bom, approvalComment);
      setApprovalComment('');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden" data-testid={`pending-bom-${bom.bom_id}`}>
      {/* Header Row */}
      <div className="p-4 flex items-center gap-4">
        <button 
          onClick={() => setExpanded(!expanded)} 
          className="p-1 hover:bg-slate-700 rounded transition-colors"
        >
          {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-blue-400 font-semibold">{bom.bom_number}</span>
            <StatusBadge status={bom.status} />
            <StageBadge stage={bom.current_approval_stage} />
          </div>
          <p className="text-slate-300 text-sm mt-1 truncate">{bom.product_name}</p>
        </div>
        
        <div className="text-right">
          <div className="text-sm">
            <span className="text-slate-500">Cost: </span>
            <span className={`font-mono font-semibold ${budgetColor}`}>
              ₹{bom.cost_total?.toLocaleString() || 0}
            </span>
            {bom.budget_target > 0 && (
              <span className="text-slate-500 ml-1">({budgetPct}%)</span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {bom.line_count} lines • Rev {bom.revision_number}
          </div>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => onViewBom(bom)}
          className="ml-2"
          data-testid={`view-bom-${bom.bom_id}`}
        >
          <FileText className="w-4 h-4 mr-1" /> View
        </Button>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-slate-700 p-4 space-y-4 animate-in fade-in duration-200">
          {/* Submission Info */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              <span className="text-slate-400">Submitted by:</span>
              <span className="text-slate-200">{bom.submitted_by_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-slate-400">on</span>
              <span className="text-slate-200">
                {bom.submitted_at ? new Date(bom.submitted_at).toLocaleString() : 'N/A'}
              </span>
            </div>
          </div>
          
          {/* Approval History */}
          {bom.approvals && bom.approvals.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Approval History
              </h4>
              <ApprovalTimeline approvals={bom.approvals} />
            </div>
          )}
          
          {/* Comment Input */}
          <div>
            <label className="text-xs text-slate-500 block mb-1">Comments (optional)</label>
            <textarea
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
              placeholder="Add comments for your approval decision..."
              className="w-full bg-slate-900/50 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              rows={2}
              data-testid={`approval-comment-${bom.bom_id}`}
            />
          </div>
          
          {/* Action Buttons */}
          {!showRejectForm ? (
            <div className="flex items-center gap-3">
              <Button 
                onClick={handleApprove}
                disabled={processing}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid={`approve-btn-${bom.bom_id}`}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {processing ? 'Processing...' : 'Approve'}
              </Button>
              <Button 
                onClick={handleReturnToDraft}
                disabled={processing}
                variant="outline"
                className="text-amber-400 border-amber-600 hover:bg-amber-600/20"
                data-testid={`return-draft-btn-${bom.bom_id}`}
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Return to Draft
              </Button>
              <Button 
                onClick={() => setShowRejectForm(true)}
                disabled={processing}
                variant="outline"
                className="text-red-400 border-red-600 hover:bg-red-600/20"
                data-testid={`reject-btn-${bom.bom_id}`}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </div>
          ) : (
            <div className="space-y-3 p-3 bg-red-900/10 border border-red-800/30 rounded">
              <h4 className="text-sm font-medium text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Rejection Reason (Required)
              </h4>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this BOM is being rejected (min 10 characters)..."
                className="w-full bg-slate-900/50 border border-red-700/50 rounded px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                rows={3}
                data-testid={`reject-reason-${bom.bom_id}`}
              />
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleReject}
                  disabled={processing || rejectReason.length < 10}
                  className="bg-red-600 hover:bg-red-500 text-white"
                  data-testid={`confirm-reject-btn-${bom.bom_id}`}
                >
                  {processing ? 'Processing...' : 'Confirm Rejection'}
                </Button>
                <Button 
                  onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                  variant="ghost"
                  className="text-slate-400"
                >
                  Cancel
                </Button>
                <span className="text-xs text-slate-500 ml-2">
                  {rejectReason.length}/10 characters minimum
                </span>
              </div>
            </div>
          )}
          
          {/* AS9100D Clause Reference */}
          <div className="text-[10px] text-slate-600 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            AS9100D 8.3.4: Your approval constitutes an electronic signature
          </div>
        </div>
      )}
    </div>
  );
};

/* Main Approval Dashboard Page */
const ApprovalDashboard = () => {
  const navigate = useNavigate();
  const [pendingBoms, setPendingBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);
  
  const fetchPendingApprovals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [approvalRes, permRes] = await Promise.all([
        fetchWithAuth(`${API}/api/v1/bom/approvals/pending`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }),
        fetchWithAuth(`${API}/api/v1/bom/approvals/permissions`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }),
      ]);
      if (!approvalRes.ok) throw new Error('Failed to fetch pending approvals');
      setPendingBoms(await approvalRes.json());
      if (permRes.ok) setPermissions(await permRes.json());
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchPendingApprovals();
  }, [fetchPendingApprovals]);

  // Filter BOMs based on user's permissions
  const filterableStages = permissions?.allowed_stages || {};
  const bomsUserCanApprove = pendingBoms.filter(b => filterableStages[b.current_approval_stage]);
  const bomsUserCannotApprove = pendingBoms.filter(b => !filterableStages[b.current_approval_stage]);
  
  const handleApprove = async (bom, comments) => {
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bom.bom_id}/revisions/${bom.revision_id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision: 'Approved',
          comments,
          meaning_statement: 'I have reviewed this BOM revision and confirm it meets requirements.',
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Approval failed');
      }
      const data = await res.json();
      toast.success(`BOM ${bom.bom_number} approved! Status: ${data.status}`);
      fetchPendingApprovals();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const handleReject = async (bom, reason) => {
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bom.bom_id}/revisions/${bom.revision_id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rejection_reason: reason,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Rejection failed');
      }
      toast.success(`BOM ${bom.bom_number} rejected and returned to draft`);
      fetchPendingApprovals();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const handleReturnToDraft = async (bom, comments) => {
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/${bom.bom_id}/revisions/${bom.revision_id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          decision: 'ReturnedToDraft',
          comments,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Return to draft failed');
      }
      toast.success(`BOM ${bom.bom_number} returned to draft for corrections`);
      fetchPendingApprovals();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const handleViewBom = (bom) => {
    navigate(`/bom/${bom.bom_id}`);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="approval-dashboard">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                BOM Approval Dashboard
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Review and approve pending BOM revisions • AS9100D 8.3.4 Workflow
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={fetchPendingApprovals}
                variant="outline"
                size="sm"
                disabled={loading}
              >
                <RotateCcw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                onClick={() => navigate('/bom')}
                variant="outline"
                size="sm"
              >
                Back to BOM Explorer
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-amber-900/20 border border-amber-700/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-400" />
              <div>
                <div className="text-2xl font-bold text-amber-400">{pendingBoms.length}</div>
                <div className="text-xs text-amber-300/70">Pending Approval</div>
              </div>
            </div>
          </div>
          <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <User className="w-8 h-8 text-blue-400" />
              <div>
                <div className="text-2xl font-bold text-blue-400">
                  {pendingBoms.filter(b => b.current_approval_stage === 'Engineering_Lead').length}
                </div>
                <div className="text-xs text-blue-300/70">Engineering Review</div>
              </div>
            </div>
          </div>
          <div className="bg-violet-900/20 border border-violet-700/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-violet-400" />
              <div>
                <div className="text-2xl font-bold text-violet-400">
                  {pendingBoms.filter(b => b.current_approval_stage === 'Quality_Rep').length}
                </div>
                <div className="text-xs text-violet-300/70">Quality Review</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-slate-400">Loading pending approvals...</span>
          </div>
        )}
        
        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-6 text-center">
            <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-red-400">{error}</p>
            <Button onClick={fetchPendingApprovals} variant="outline" size="sm" className="mt-3">
              Try Again
            </Button>
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && pendingBoms.length === 0 && (
          <div className="bg-slate-800/30 border border-slate-700 rounded-lg p-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-slate-200">All Caught Up!</h3>
            <p className="text-slate-400 mt-1">No BOM revisions pending approval.</p>
            <Button onClick={() => navigate('/bom')} variant="outline" size="sm" className="mt-4">
              Go to BOM Explorer
            </Button>
          </div>
        )}
        
        {/* Permission Info */}
        {!loading && permissions && (
          <div className="mb-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400">Your role:</span>
              <Badge className="bg-slate-700 text-slate-200">{permissions.user_role}</Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {permissions.can_approve_engineering && (
                <span className="flex items-center gap-1 text-blue-400">
                  <CheckCircle className="w-3 h-3" /> Engineering
                </span>
              )}
              {permissions.can_approve_quality && (
                <span className="flex items-center gap-1 text-violet-400">
                  <CheckCircle className="w-3 h-3" /> Quality
                </span>
              )}
            </div>
          </div>
        )}
        
        {/* BOMs User Can Approve */}
        {!loading && !error && bomsUserCanApprove.length > 0 && (
          <div className="space-y-4 mb-8">
            <h2 className="text-lg font-medium text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Your Queue ({bomsUserCanApprove.length})
            </h2>
            {bomsUserCanApprove.map((bom) => (
              <PendingBomCard
                key={bom.revision_id}
                bom={bom}
                onApprove={handleApprove}
                onReject={handleReject}
                onReturnToDraft={handleReturnToDraft}
                onViewBom={handleViewBom}
              />
            ))}
          </div>
        )}
        
        {/* BOMs User Cannot Approve (View Only) */}
        {!loading && !error && bomsUserCannotApprove.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-medium text-slate-400 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Awaiting Other Approvers ({bomsUserCannotApprove.length})
            </h2>
            <p className="text-xs text-slate-500 mb-2">
              These BOMs are awaiting approval from other roles. You can view but not approve.
            </p>
            {bomsUserCannotApprove.map((bom) => (
              <div key={bom.revision_id} className="bg-slate-800/30 border border-slate-700 rounded-lg p-4 opacity-70">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-mono text-blue-400 font-semibold">{bom.bom_number}</span>
                      <StatusBadge status={bom.status} />
                      <StageBadge stage={bom.current_approval_stage} />
                    </div>
                    <p className="text-slate-400 text-sm mt-1">{bom.product_name}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Submitted by {bom.submitted_by_name} • {bom.line_count} lines
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleViewBom(bom)}
                    className="text-slate-400"
                  >
                    <FileText className="w-4 h-4 mr-1" /> View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalDashboard;
